/* eslint-disable camelcase */
import mixpanel from "mixpanel-browser";

import { getLangCode } from "../config/index";
import routeCodes from "../config/routeCodes";
import { mobile } from "./detection";
import {
  abTestConfig,
  abTestLocalStorageKey,
  adData,
  language,
  mixpanelProjectToken,
  sessionGameplaysKey,
  userRoles,
} from "../config/constants";
import { getItem, removeItem, setItem } from "./localStorage";
import { convertEPOCHDate } from ".";

// Initialize Mixpanel
export const initMixpanel = () => {
  mixpanel.init(mixpanelProjectToken, {
    debug: process.env.NODE_ENV !== "production",
    persistence: "localStorage",
  });

  // Add Super Properties for Mixpanel, which are shared by all events. Some of these may be updated later.
  mpRegisterEventSuperProperties({
    is_mobile: mobile,
    ui_lang: getLangCode() === language.default ? language.default : "es",
  });
};

// Mapping from ABcya tokenRole to Mixpanel user_role
export const getMixpanelUserRole = (tokenRole) => {
  switch (tokenRole) {
    case "STUDENT":
      return "student";
    case "FAMILY":
      return "parent";
    case "CLASSROOM":
      return "teacher";
    case "SCHOOL":
      return "teacher";
    default:
      return "anonymous";
  }
};

// Mapping from ABcya planGroup to Mixpanel membership_type
export const getMixpanelMembershipType = (planGroup) => {
  switch (planGroup) {
    case "PUBLIC":
      return "free";
    case "CLASSROOM":
      return "premium classroom";
    case "SCHOOL":
      return "premium school";
    case "FAMILY":
      return "premium family";
    default:
      return "none";
  }
};

// Count ad elements on the page
const countAdElements = () => {
  let totalCount = 0;

  const hasContent = (elementId) => {
    const element = document.querySelector(`#${elementId}`);
    return element && element.innerHTML.trim() !== "";
  };

  // Count desktop and mobile leader ads
  totalCount += [...adData.divIds.leader.desktop, ...adData.divIds.leader.mobile].reduce(
    (sum, position) => sum + (hasContent(position) ? 1 : 0),
    0,
  );
  // Count sky ads
  totalCount += Object.values(adData.divIds.sky).reduce((sum, position) => sum + (hasContent(position) ? 1 : 0), 0);
  // Count billboard ads
  totalCount += hasContent(adData.divIds.billboard) ? 1 : 0;
  // Count adhesion ads
  totalCount += hasContent(adData.divIds.adhesion) ? 1 : 0;
  // Count video ads
  totalCount += Object.values(adData.divIds.video).reduce((sum, position) => sum + (hasContent(position) ? 1 : 0), 0);

  return totalCount;
};

const billingFrequency = (months) => {
  switch (parseInt(months)) {
    case 1:
      return "monthly";
    case 6:
      return "semiannually";
    case 12:
      return "annually";
    case 24:
      return "24-months";
    case 36:
      return "36-months";
    default:
      return "unknown";
  }
};

const getCurrentUrl = (pathname) => {
  return `${window.location.protocol}//${window.location.host}${pathname}`;
};

export const setUserPropertyNumAdsShown = () => {
  // set a timeout to count the number of ads shown
  try {
    setTimeout(() => {
      const numOfAds = countAdElements();
      mixpanel.people.set_once({
        ads_shown: 0,
      });
      mixpanel.people.increment("ads_shown", numOfAds);
    }, 2000);
  } catch (error) {}
};

/**
 * Checks to see if a game event properties are valid. This will send a game_analytics_error event to Mixpanel if there is invalid data.
 * @param {object} properties the game properties to check
 * @param {string} properties.id the game id
 * @param {string} properties.fullname the game fullname
 * @param {string} properties.gamePageCurrentURL the game page current URL
 * @returns {boolean} whether the game event properties are valid
 */
const isGameEventPropertyValuesValid = (properties) => {
  const { id, fullname, gamePageCurrentURL } = properties;

  if (
    !id ||
    !fullname ||
    !gamePageCurrentURL ||
    id.length === 0 ||
    fullname.length === 0 ||
    gamePageCurrentURL.length === 0
  ) {
    try {
      mixpanel.track("game_analytics_error", {
        invalid_game_id: id,
        invalid_game_name: fullname,
        invalid_current_url: gamePageCurrentURL,
        possible_current_url: window.location.href,
        possible_current_path: window.location.pathname,
      });
    } catch (e) {
      console.log("Error in isGameEventPropertyValuesValid: ", e);
    }
    return false;
  }

  return true;
};

export const getErrorMessageText = (error) => {
  if (!error) {
    return "";
  }

  try {
    if (typeof error === "string") {
      return error;
    }

    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      return error.graphQLErrors[0].message;
    }

    if (error?.message) {
      return error.message;
    }

    return error.toString();
  } catch (e) {
    return "";
  }
};

export const inferSubmissionFailureReasonAndType = (message) => {
  // We are not adding these strings to the EN/LA string files because:
  // 1. They are not used in the app UI and are only used for Mixpanel analytics.
  // 2. Error messages from our API or third-party services are not stable and may change over time.
  try {
    const normalizedMessage = message.toLowerCase();
    const includesAnyToken = (text, tokens) => tokens.some((token) => text.includes(token));
    const inferenceRules = [
      {
        matches: (text) => includesAnyToken(text, ["network", "connection", "timeout", "timed out", "recaptcha"]),
        reason: "connectivity_issue",
        errorType: "other",
      },
      {
        matches: (text) =>
          includesAnyToken(text, [
            "email already in use",
            "este email ya está en uso",
            "user with this email already exists",
            "ya existe un usuario con este email",
          ]),
        reason: "invalid_field",
        errorType: "email_in_use",
      },
      {
        matches: (text) => includesAnyToken(text, ["access name", "nombre de acceso"]),
        reason: "invalid_field",
        errorType: "invalid_access_name",
      },
      {
        matches: (text) =>
          (text.includes("card") && text.includes("code")) || (text.includes("código") && text.includes("tarjeta")),
        reason: "invalid_field",
        errorType: "invalid_cvv",
      },
      {
        matches: (text) => includesAnyToken(text, ["expiration", "vencimiento"]),
        reason: "invalid_field",
        errorType: "invalid_expiration",
      },
      {
        matches: (text) =>
          (text.includes("card") && text.includes("incomplete")) ||
          (text.includes("incompleto") && text.includes("tarjeta")),
        reason: "invalid_field",
        errorType: "invalid_card_number",
      },
      {
        matches: (text) => includesAnyToken(text, ["payment", "declined"]),
        reason: "payment_failure",
        errorType: "invalid_card",
      },
    ];

    const matchedRule = inferenceRules.find((rule) => rule.matches(normalizedMessage));
    if (matchedRule) {
      return { reason: matchedRule.reason, errorType: matchedRule.errorType };
    }

    return { reason: "other", errorType: "other" };
  } catch (e) {
    return { reason: "other", errorType: "other" };
  }
};

const inlineValidationErrorTypeByField = {
  name: "invalid_name",
  email: "invalid_email_format",
  phone: "invalid_phone",
  password: "invalid_password",
  position: "invalid_position",
  country: "invalid_country",
  schoolName: "invalid_school_name",
  schoolAddress: "invalid_school_address",
  deviceCount: "invalid_device_count",
  studentCount: "invalid_student_count",
  card_number: "invalid_card_number",
  card_expiration: "invalid_expiration",
  accessName: "invalid_access_name",
  accessCode: "invalid_access_code",
};

export const inferInlineValidationErrorType = (fieldName) => inlineValidationErrorTypeByField[fieldName] || "other";

// Track page view event
export const mpPageView = () => {
  try {
    // Track total pageviews using localStorage
    let totalPageviews = getItem("total_pageviews");
    if (!totalPageviews) {
      setItem("total_pageviews", 1);
      totalPageviews = 1;
    } else {
      setItem("total_pageviews", String(Number(totalPageviews) + 1));
      totalPageviews = Number(totalPageviews) + 1;
    }

    // Check if pageviewcount exists in sessionStorage. (pageviewcount is set by Mixpanel on the first pageview.)
    let sessionPageviews = sessionStorage.getItem("pageviewcount");
    if (!sessionPageviews) {
      sessionPageviews = 1;
    } else {
      sessionPageviews = Number(sessionPageviews) + 1;
    }

    // Set the same delay as gtmPageView to allow Helmet to correctly set the page title
    setTimeout(() => {
      mixpanel.track_pageview({
        session_pageviews: sessionPageviews,
        total_pageviews: totalPageviews,
      });
    }, 100);
  } catch (e) {}
};

export const mpUserIdentify = (userId) => {
  mixpanel.identify(userId);
};

export const mpSetUserProperties = (properties) => {
  mixpanel.people.set(properties);
};

export const mpUserIdentifyAndSetProperties = (userId, properties) => {
  mixpanel.identify(userId);
  mixpanel.people.set(properties);
};

export const mpUserReset = () => {
  try {
    mixpanel.reset();
    // Set A/B Test-related super properties again after resetting Mixpanel
    const mpABTests = JSON.parse(getItem(abTestLocalStorageKey));
    if (mpABTests) {
      const expCookie = Object.values(mpABTests).reduce(
        (cookie, testVariant) => ({
          ...cookie,
          [testVariant.testName]: testVariant.testVariantName,
        }),
        {},
      );
      mpRegisterEventSuperProperties({
        experiments_cookie: expCookie,
      });
    }
  } catch (error) {
    console.log("Error in mpUserReset: ", error);
  }
};

export const mpRegisterEventSuperProperties = (properties) => {
  try {
    mixpanel.register(properties);
  } catch (error) {}
};

export const mpLoginSuccess = (tokenRole, loginCount, isLoginByForm, planGroup, userId, createdAt, pathname) => {
  try {
    const mpUserRole = getMixpanelUserRole(tokenRole);
    const mpMembershipType = getMixpanelMembershipType(planGroup);
    mpUserIdentifyAndSetProperties(userId, {
      signup_date: createdAt,
      signup_language: getLangCode() === language.default ? language.default : "es",
      user_role: mpUserRole,
      membership_type: mpMembershipType,
      last_active_date: new Date().toISOString(),
    });
    mpRegisterEventSuperProperties({
      user_role: mpUserRole,
      membership_type: mpMembershipType,
    });

    const isNewSignup = getItem("isNewSignup") === "1";
    removeItem("isNewSignup"); // remove it after checking
    let loginMethod;
    let loginPagePath;
    let loginDisplayType;
    // manual login vs token-based login
    if (isLoginByForm) {
      loginMethod = tokenRole === "STUDENT" ? "access code" : "password";
      // new signup vs returning user
      if (isNewSignup) {
        switch (tokenRole) {
          case userRoles.free:
            loginPagePath = routeCodes.SUBSCRIBE_FREE;
            break;
          case userRoles.family:
            loginPagePath = routeCodes.SUBSCRIBE_FAMILY;
            break;
          case userRoles.classroom:
            loginPagePath = routeCodes.SUBSCRIBE_CLASSROOM;
            break;
          case userRoles.school:
            loginPagePath = routeCodes.SUBSCRIBE_SCHOOL;
            break;
          default:
            loginPagePath = "/";
        }
        loginDisplayType = "page";
      } else {
        loginPagePath = window.location.pathname;
        loginDisplayType = "modal";
      }
    } else {
      loginMethod = "token-based";
      loginPagePath = window.location.pathname;
      loginDisplayType = "auto-login";
    }

    mixpanel.track("Login Success", {
      login_origin_url: window.location.origin + loginPagePath,
      page_url_path: loginPagePath,
      login_display_type: loginDisplayType,
      login_count: loginCount,
      login_method: loginMethod,
      $current_url: getCurrentUrl(pathname),
    });
    mixpanel.people.set_once({
      total_sessions: 0,
    });
    mixpanel.people.increment("total_sessions", 1);
  } catch (e) {}
};

export const mpPaymentSubmitted = (subData) => {
  try {
    const months = parseInt(subData.months.slice(1));

    const data = {
      subscription_origin_url: window.location.href,
      page_url_path: window.location.pathname,
      subscription_plan_type: getMixpanelMembershipType(subData.planGroup),
      subscription_billing_frequency: billingFrequency(months),
      price: subData.amount,
      $current_url: getCurrentUrl(subData.pathname),
    };

    if (subData.elapsed) {
      data.time_on_page_seconds = subData.elapsed;
    }

    if (typeof subData.isRetry === "boolean") {
      data.is_retry = subData.isRetry;
    }

    mixpanel.track("Payment Submitted", data);
  } catch (e) {}
};

export const mpSubscriptionFailed = (subData) => {
  try {
    const months = parseInt(subData.months.slice(1));

    mixpanel.track("Subscription Failed", {
      reason: subData.reason || "other",
      error_type: subData.errorType || "other",
      message_displayed: subData.messageDisplayed || "",
      subscription_billing_frequency: billingFrequency(months),
      subscription_plan_type: getMixpanelMembershipType(subData.planGroup),
    });
  } catch (e) {}
};

export const mpFieldValidationFailed = (fieldData) => {
  try {
    mixpanel.track("Field Validation Failed", {
      field_name: fieldData.fieldName || "",
      error_type: fieldData.errorType || "",
      error_message_displayed: fieldData.errorMessageDisplayed || "",
      form: fieldData.form || "",
    });
  } catch (e) {}
};

export const mpCreateAccountCTASelected = (data) => {
  try {
    mixpanel.track("Create Account CTA Selected", {
      subscription_plan_type: getMixpanelMembershipType(data.planGroup),
      subscription_billing_frequency: billingFrequency(data.subMonths),
      time_on_page_seconds: data.timeOnPageSeconds,
      modal_trigger_type: data.modalTriggerType || "",
    });
  } catch (e) {}
};

export const mpAccountCreationSucceeded = (data) => {
  try {
    mixpanel.track("Account Creation Succeeded", {
      subscription_plan_type: getMixpanelMembershipType(data.planGroup),
      subscription_billing_frequency: billingFrequency(data.subMonths),
      modal_trigger_type: data.modalTriggerType || "",
    });
  } catch (e) {}
};

export const mpAccountCreationFailed = (data) => {
  try {
    mixpanel.track("Account Creation Failed", {
      subscription_plan_type: getMixpanelMembershipType(data.planGroup),
      subscription_billing_frequency: billingFrequency(data.subMonths),
      modal_trigger_type: data.modalTriggerType || "",
      errors: data.errors || [],
    });
  } catch (e) {}
};

export const mpModalClicked = (modalData) => {
  try {
    mixpanel.track("Modal Clicked", {
      page_title: document.title,
      page_url_path: window.location.pathname,
      modal_type: modalData.modalType || "",
      modal_trigger_type: modalData.triggerType || "",
      modal_header: modalData.header || "",
      modal_primary_cta: modalData.primaryCta || "",
      modal_secondary_cta: modalData.secondaryCta || "",
      modal_tertiary_cta: modalData.tertiaryCta || "",
      modal_is_dismissible: modalData.isDismissible || false,
      modal_interaction: modalData.interaction || "",
      $current_url: getCurrentUrl(modalData.pathname),
    });
  } catch (e) {}
};

export const mpModalShown = (modalData) => {
  try {
    mixpanel.track("Modal Shown", {
      page_title: document.title,
      page_url_path: window.location.pathname,
      modal_type: modalData.modalType || "",
      modal_trigger_type: modalData.triggerType || "",
      modal_header: modalData.header || "",
      modal_primary_cta: modalData.primaryCta || "",
      modal_secondary_cta: modalData.secondaryCta || "",
      modal_tertiary_cta: modalData.tertiaryCta || "",
      modal_is_dismissible: modalData.isDismissible || false,
      $current_url: getCurrentUrl(modalData.pathname),
    });
  } catch (e) {}
};

export const mpSignUpSuccess = (signupDisplayType, planGroup, pathname) => {
  try {
    setItem("isNewSignup", "1"); // used for Login Success event to check if it's a new signup
    const mpMembershipType = getMixpanelMembershipType(planGroup);
    mixpanel.track("Signup Success", {
      signup_origin_url: window.location.href,
      page_url_path: window.location.pathname,
      signup_display_type: signupDisplayType,
      membership_type: mpMembershipType,
      $current_url: getCurrentUrl(pathname),
    });
    mixpanel.people.set({
      signup_date: new Date().toISOString(),
      signup_language: getLangCode(),
    });
  } catch (e) {
    console.log("Error in mpSignUpSuccess: ", e);
  }
};

export const mpSubCTASelect = (feature, ctaLocation, ctaText, pathname) => {
  try {
    mixpanel.track("Subscription CTA Selected", {
      page_title: document.title,
      locked_feature: feature,
      subscription_cta_location: ctaLocation,
      subscription_cta_text: ctaText,
      page_url_path: window.location.pathname,
      $current_url: getCurrentUrl(pathname),
    });
    mpRegisterEventSuperProperties({
      last_subscription_cta_location: ctaLocation,
      last_subscription_cta_text: ctaText,
    });
    mpSetUserProperties({
      last_subscription_cta_location: ctaLocation,
    });
  } catch (e) {
    console.log("Error in mpSubCTASelect: ", e);
  }
};

export const mpSubStarted = (subData) => {
  try {
    const mpMembershipType = getMixpanelMembershipType(subData.planGroup);

    mpRegisterEventSuperProperties({
      membership_type: mpMembershipType,
    });

    mixpanel.track("Subscription Started", {
      subscription_billing_frequency: billingFrequency(subData.months),
      subscription_plan_type: mpMembershipType,
      subscription_price: subData.amount,
      subscription_start_type: subData.startType,
      subscription_start_source: subData.startSource || "web_checkout",
      subscription_term_start: convertEPOCHDate(Number(subData.periodStart), language.default),
      subscription_term_end: convertEPOCHDate(Number(subData.periodEnd), language.default),
      subscription_origin_url: window.location.href,
      page_url_path: window.location.pathname,
      $current_url: getCurrentUrl(subData.pathname),
    });

    mpSetUserProperties({
      subscription_billing_frequency: billingFrequency(subData.months),
      subscription_purchased_in_web: true,
      subscription_renewal_status: "auto renewing",
      subscription_term_start: convertEPOCHDate(Number(subData.periodStart), language.default),
      subscription_term_end: convertEPOCHDate(Number(subData.periodEnd), language.default),
      subscription_status: "active",
      membership_type: mpMembershipType,
    });

    mixpanel.people.set_once({
      subscription_original_start_date: convertEPOCHDate(Number(subData.subStartDate), language.default),
    });
  } catch (e) {
    console.log("Error in mpSubStarted: ", e);
  }
};

const daysUntil = (epochTime) => {
  const now = Date.now(); // Get current time in milliseconds
  const diff = epochTime * 1000 - now; // Convert epochTime to milliseconds and find difference
  return Math.ceil(diff / (1000 * 60 * 60 * 24)); // Convert to days and round up
};

export const mpSubAutoRenewCancelled = (subData) => {
  try {
    mixpanel.track("Subscription Autorenew Cancelled", {
      subscription_time_remaining: daysUntil(Number(subData.periodEnd)),
      subscription_purchase_platform: "website",
      subscription_renewal_count: subData.countOfRenewalInvoices,
      subscription_original_start_date: convertEPOCHDate(Number(subData.subStartDate), language.default),
      subscription_term_start: convertEPOCHDate(Number(subData.periodStart), language.default),
      subscription_term_end: convertEPOCHDate(Number(subData.periodEnd), language.default),
      subscription_billing_frequency: billingFrequency(subData.months),
      subscription_plan_type: getMixpanelMembershipType(subData.planGroup),
      $current_url: getCurrentUrl(subData.pathname),
    });

    mpSetUserProperties({
      subscription_renewal_status: "canceled",
    });
  } catch (e) {
    console.log("Error in mpSubAutoRenewCancelled: ", e);
  }
};

export const mpGameStarted = (game, isReplay, gameHasCompletionState, wasCompleted) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

  if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
    return;
  }

  try {
    let sessionPlayCount = Number(window.sessionStorage.getItem(sessionGameplaysKey)) || 0;
    window.sessionStorage.setItem(sessionGameplaysKey, String(++sessionPlayCount));

    mixpanel.track("Game Started", {
      game_genre: genre,
      game_id: id,
      is_replay: isReplay,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      session_gameplays: sessionPlayCount,
      game_has_completion_state: gameHasCompletionState,
      game_has_been_completed: wasCompleted,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameStarted: ", e);
  }
};

export const mpPlayPressed = (game) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

  if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
    return;
  }

  try {
    const sessionPlayCount = Number(window.sessionStorage.getItem(sessionGameplaysKey)) || 0;

    mixpanel.track("Play Button Pressed", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      session_gameplays: sessionPlayCount,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpPlayPressed: ", e);
  }
};

export const mpGamePaused = (game, pauseReason, gameHasCompletionState, wasCompleted) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

  if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
    return;
  }

  try {
    mixpanel.track("Game Paused", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      pause_reason: pauseReason,
      game_has_completion_state: gameHasCompletionState,
      game_has_been_completed: wasCompleted,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGamePaused: ", e);
  }
};

export const mpGameResumed = (game, timeElapsedSeconds, gameHasCompletionState, wasCompleted) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

  if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
    return;
  }

  try {
    mixpanel.track("Game Resumed", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      time_elapsed_seconds: timeElapsedSeconds,
      game_has_completion_state: gameHasCompletionState,
      game_has_been_completed: wasCompleted,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameResumed: ", e);
  }
};

export const mpGameCompleted = (game, activePlayTimeSeconds, wasCompleted, completionOutcome) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

  if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
    return;
  }

  try {
    mixpanel.track("Game Completed", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      active_play_time_seconds: activePlayTimeSeconds,
      game_has_been_completed: wasCompleted,
      completion_outcome: completionOutcome,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameCompleted: ", e);
  }
};

export const mpLevelCompleted = (
  game,
  activePlayTimeSeconds,
  level,
  totalGameLevels,
  attempts,
  wasCompleted,
  completionOutcome,
) => {
  const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

  if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
    return;
  }

  try {
    mixpanel.track("Level Completed", {
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      developers,
      active_play_time_seconds: activePlayTimeSeconds,
      level,
      total_game_levels: totalGameLevels,
      attempts,
      game_has_been_completed: wasCompleted,
      completion_outcome: completionOutcome,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpLevelCompleted: ", e);
  }
};

export const mpGameAbandoned = (game, activePlayTimeSeconds, exitMethod, gameHasCompletionState, wasCompleted) => {
  try {
    const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

    if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
      return;
    }

    mixpanel.track("Game Abandoned", {
      active_play_time_seconds: activePlayTimeSeconds,
      developers,
      exit_method: exitMethod,
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      game_has_completion_state: gameHasCompletionState,
      game_has_been_completed: wasCompleted,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameAbandoned: ", e);
  }
};

export const mpTimeOnPage = (timeOnPageSeconds, scrollDepth, numAdsShown, isBlocking, numAdsOnPage, pathname) => {
  try {
    mixpanel.track("Time On Page", {
      time_on_page_seconds: timeOnPageSeconds,
      scroll_depth: scrollDepth,
      num_ads_shown: numAdsShown,
      is_blocking: isBlocking,
      num_ads_on_page: numAdsOnPage,
      $current_url: getCurrentUrl(pathname),
    });
  } catch (e) {
    console.log("Error in mpTimeOnPage: ", e);
  }
};

export const mpGameToolbarAction = (game, actionType) => {
  try {
    const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

    if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
      return;
    }

    mixpanel.track("Game Toolbar Action Triggered", {
      action_type: actionType,
      developers,
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameToolbarAction: ", e);
  }
};

export const mpGameAnalytics = (game, eventName, props) => {
  try {
    const { genre, id, subject, theme, category, fullname, gamePageCurrentURL, developers } = game;

    if (!isGameEventPropertyValuesValid({ id, fullname, gamePageCurrentURL })) {
      return;
    }

    mixpanel.track(eventName, {
      ...props,
      developers,
      game_genre: genre,
      game_id: id,
      game_subject: subject,
      game_theme: theme,
      game_category: category,
      game_name: fullname,
      $current_url: gamePageCurrentURL,
    });
  } catch (e) {
    console.log("Error in mpGameAnalytics: ", e);
  }
};

// An utility function to find or assign the device into one of two groups for A/B testing
// If the game is part of an active A/B test, return an object with all active A/B tests and their assigned variants
export const assignDeviceForABTest = (gameShortname) => {
  try {
    if (abTestConfig[gameShortname]) {
      const mp_ab_test = JSON.parse(getItem(abTestLocalStorageKey)) || {};
      // If the test is already assigned to a device in local storage, return all test variants
      if (mp_ab_test[gameShortname]) {
        return mp_ab_test;
      }

      // If the test is not assigned to a device in local storage, randomly assign one, and save
      let gameVariant = {};
      const config = abTestConfig[gameShortname];
      const rand = Math.random();
      if (rand < config.variants.groupA.percentage) {
        gameVariant = {
          testName: config.testName,
          testVariantName: config.variants.groupA.name,
          gameVersion: config.variants.groupA.gameVersion,
        };
      } else {
        gameVariant = {
          testName: config.testName,
          testVariantName: config.variants.groupB.name,
          gameVersion: config.variants.groupB.gameVersion,
        };
      }
      mp_ab_test[gameShortname] = gameVariant;
      setItem(abTestLocalStorageKey, JSON.stringify(mp_ab_test));
      return mp_ab_test;
    }
    return null;
  } catch (error) {
    console.log("Error in assignDeviceForABTest: ", error);
    return null;
  }
};

export const cleanABTestLocalStorage = () => {
  try {
    // remove all entries whose key (i.e. gameShortname) is not in the abTestConfig
    const mpABTests = JSON.parse(getItem(abTestLocalStorageKey) || "{}");
    const config = Object.keys(abTestConfig).reduce((remainingTests, gameShortname) => {
      if (mpABTests[gameShortname]) {
        return { ...remainingTests, [gameShortname]: mpABTests[gameShortname] };
      }
      return remainingTests;
    }, {});
    if (Object.keys(config).length > 0) {
      setItem(abTestLocalStorageKey, JSON.stringify(config));
      // Update the super property "experiments_cookie"
      const expCookie = Object.values(config).reduce(
        (cookie, testVariant) => ({
          ...cookie,
          [testVariant.testName]: testVariant.testVariantName,
        }),
        {},
      );
      mpRegisterEventSuperProperties({
        experiments_cookie: expCookie,
      });
    } else {
      removeItem(abTestLocalStorageKey);
      mixpanel.unregister("experiments_cookie");
    }
  } catch (error) {
    console.log("Error in cleanABTestLocalStorage: ", error);
  }
};
